import { useState, useEffect, useCallback } from 'react'
import { useNavigate } from 'react-router-dom'
import { getApiUrl, fetchWithAuth } from '@/api'

interface ProgramListItem {
  program_id: number
  program_name: string
  thumbnail_url: string | null
  capacity: number
  location: string | null
  apply_start: string | null
  apply_end: string | null
  has_sessions: boolean
  is_visible: number
  is_closed: number
}

const LIMIT = 20

export default function ProgramList() {
  const navigate = useNavigate()
  const [programs, setPrograms] = useState<ProgramListItem[]>([])
  const [total, setTotal] = useState(0)
  const [loading, setLoading] = useState(true)
  const [searchInput, setSearchInput] = useState('')
  const [searchKeyword, setSearchKeyword] = useState('')
  const [skip, setSkip] = useState(0)
  const [deletingId, setDeletingId] = useState<number | null>(null)
  const [excelLoadingId, setExcelLoadingId] = useState<number | null>(null)

  const fetchPrograms = useCallback(async (keyword: string, skipVal: number) => {
    setLoading(true)
    try {
      const params = new URLSearchParams()
      params.append('skip', String(skipVal))
      params.append('limit', String(LIMIT))
      const res = await fetchWithAuth(getApiUrl(`/marubit/api/program/list?${params.toString()}`))
      if (!res.ok) throw new Error('조회 실패')
      const data = await res.json()
      const filtered = keyword
        ? data.programs.filter((p: ProgramListItem) =>
            p.program_name.toLowerCase().includes(keyword.toLowerCase())
          )
        : data.programs
      setPrograms(filtered)
      setTotal(keyword ? filtered.length : data.total)
    } catch {
      alert('목록을 불러오는 데 실패했습니다.')
    } finally {
      setLoading(false)
    }
  }, [])

  useEffect(() => {
    fetchPrograms(searchKeyword, skip)
  }, [searchKeyword, skip, fetchPrograms])

  const handleSearch = () => {
    setSearchKeyword(searchInput)
    setSkip(0)
  }

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') handleSearch()
  }

  const handleResetSearch = () => {
    setSearchInput('')
    setSearchKeyword('')
    setSkip(0)
  }

  const handleDelete = async (e: React.MouseEvent, id: number, name: string) => {
    e.stopPropagation()
    if (!window.confirm(`"${name}" 프로그램을 삭제하시겠습니까?\n신청 내역이 있는 경우 삭제되지 않습니다.`)) return
    setDeletingId(id)
    try {
      const res = await fetchWithAuth(getApiUrl(`/marubit/api/program/admin/${id}`), { method: 'DELETE' })
      if (!res.ok) { const err = await res.json(); throw new Error(err.detail ?? '삭제 실패') }
      fetchPrograms(searchKeyword, skip)
    } catch (e: unknown) {
      alert(e instanceof Error ? e.message : '삭제에 실패했습니다.')
    } finally {
      setDeletingId(null)
    }
  }

  const handleExcelDownload = async (e: React.MouseEvent, id: number) => {
    e.stopPropagation()
    setExcelLoadingId(id)
    try {
      const token = localStorage.getItem('access_token')
      const res = await fetch(getApiUrl(`/marubit/api/program/admin/${id}/applications/excel`), {
        headers: { Authorization: `Bearer ${token}` },
      })
      if (!res.ok) throw new Error('다운로드 실패')
      const blob = await res.blob()
      const url = window.URL.createObjectURL(blob)
      const a = document.createElement('a')
      a.href = url
      a.download = `program_applications_${id}_${new Date().toISOString().slice(0, 10).replace(/-/g, '')}.xlsx`
      a.click()
      window.URL.revokeObjectURL(url)
    } catch {
      alert('엑셀 다운로드에 실패했습니다.')
    } finally {
      setExcelLoadingId(null)
    }
  }

  const formatDate = (d: string | null) => {
    if (!d) return '-'
    return new Date(d).toLocaleDateString('ko-KR', { year: 'numeric', month: '2-digit', day: '2-digit' })
  }

  const totalPages = Math.ceil(total / LIMIT)
  const currentPage = Math.floor(skip / LIMIT) + 1

  return (
    <div className="bg-[#f5f2f0] min-h-screen p-8">
      <div className="max-w-[1200px] mx-auto">

        {/* Header */}
        <div className="flex items-start justify-between mb-6">
          <div>
            <h1 className="text-[22px] font-extrabold text-[#2a1f1a] mb-1">프로그램 관리</h1>
            <p className="text-[13px] text-gray-400">
              {new Date().toLocaleDateString('ko-KR', {
                year: 'numeric', month: 'long', day: 'numeric', weekday: 'long',
              })}
            </p>
          </div>
          <div className="text-[13px] text-gray-400 font-medium mt-1">
            총 <span className="text-[#887267] font-bold">{total}</span>개
          </div>
        </div>

        {/* Search + 등록 버튼 */}
        <div className="bg-white rounded-2xl border border-[#e8e0dc] px-5 py-4 mb-4 shadow-[0_2px_12px_rgba(136,114,103,0.06)] flex flex-wrap gap-3 items-end justify-between">
          <div className="flex items-end gap-3">
            <div className="flex flex-col gap-1">
              <label className="text-[11px] text-[#5a3d32] font-medium">프로그램명</label>
              <input
                type="text"
                value={searchInput}
                onChange={e => setSearchInput(e.target.value)}
                onKeyDown={handleKeyDown}
                placeholder="프로그램명 검색"
                className="border border-[#e8e0dc] rounded-lg px-3 py-2 text-[13px] text-[#2a1f1a] outline-none focus:border-[#887267] w-[220px] transition-colors"
              />
            </div>
            <div className="flex gap-2">
              <button
                onClick={handleSearch}
                className="bg-[#887267] text-white text-[13px] font-semibold px-4 py-2 rounded-lg border-none cursor-pointer hover:bg-[#6b5248] transition-colors h-[38px]"
              >검색</button>
              <button
                onClick={handleResetSearch}
                className="bg-white text-[#887267] text-[13px] font-semibold px-4 py-2 rounded-lg border border-[#e8e0dc] cursor-pointer hover:bg-[#f5f2f0] transition-colors h-[38px]"
              >초기화</button>
            </div>
          </div>
          <button
            onClick={() => navigate('/admin/program/create')}
            className="bg-[#887267] text-white text-[13px] font-semibold px-4 py-2 rounded-lg border-none cursor-pointer hover:bg-[#6b5248] transition-colors"
          >+ 프로그램 등록</button>
        </div>

        {/* Table */}
        <div className="bg-white rounded-2xl border border-[#e8e0dc] overflow-hidden shadow-[0_2px_12px_rgba(136,114,103,0.06)]">
          {loading ? (
            <div className="min-h-[300px] flex items-center justify-center text-gray-300 text-[13px]">
              불러오는 중...
            </div>
          ) : (
            <table className="w-full">
              <thead>
                <tr className="bg-[#ede0d8] border-b border-[#e8e0dc]">
                  <th className="text-left px-4 py-3 text-[12px] text-[#5a3d32] font-medium">프로그램명</th>
                  <th className="hidden md:table-cell text-left px-4 py-3 text-[12px] text-[#5a3d32] font-medium w-[90px]">정원</th>
                  <th className="hidden lg:table-cell text-left px-4 py-3 text-[12px] text-[#5a3d32] font-medium w-[210px]">신청기간</th>
                  <th className="text-left px-4 py-3 text-[12px] text-[#5a3d32] font-medium w-[80px]">노출</th>
                  <th className="text-left px-4 py-3 text-[12px] text-[#5a3d32] font-medium w-[80px]">상태</th>
                  <th className="text-left px-4 py-3 text-[12px] text-[#5a3d32] font-medium w-[220px]"></th>
                </tr>
              </thead>
              <tbody>
                {programs.length === 0 ? (
                  <tr>
                    <td colSpan={6} className="text-center py-12 text-gray-300 text-[13px]">
                      {searchKeyword ? '검색 결과가 없습니다.' : '등록된 프로그램이 없습니다.'}
                    </td>
                  </tr>
                ) : programs.map(p => (
                  <tr
                    key={p.program_id}
                    className="border-t border-[#f5f2f0] hover:bg-[#fdf8f0] transition-colors cursor-pointer"
                    onClick={() => navigate(`/admin/program/${p.program_id}`)}
                  >
                    <td className="px-4 py-3 text-[13px] font-medium text-[#2a1f1a]">
                      <div>{p.program_name}</div>
                      {p.location && (
                        <div className="text-[11px] text-gray-400 mt-0.5">{p.location}</div>
                      )}
                    </td>
                    <td className="hidden md:table-cell px-4 py-3 text-[13px] text-[#2a1f1a] whitespace-nowrap">
                      {p.capacity === 0 ? '무제한' : `${p.capacity}명`}
                    </td>
                    <td className="hidden lg:table-cell px-4 py-3 text-[12px] text-gray-500 whitespace-nowrap">
                      {p.apply_start && p.apply_end
                        ? `${formatDate(p.apply_start)} ~ ${formatDate(p.apply_end)}`
                        : '상시'}
                    </td>
                    <td className="px-4 py-3 whitespace-nowrap">
                      <span className={`text-[11px] px-2 py-0.5 rounded font-medium ${
                        p.is_visible ? 'bg-[#e3f2fd] text-[#1976d2]' : 'bg-[#f5f5f5] text-[#9e9e9e]'
                      }`}>
                        {p.is_visible ? '노출' : '숨김'}
                      </span>
                    </td>
                    <td className="px-4 py-3 whitespace-nowrap">
                      <span className={`text-[11px] px-2 py-0.5 rounded font-medium ${
                        p.is_closed ? 'bg-[#fce4ec] text-[#c62828]' : 'bg-[#e8f5e9] text-[#388e3c]'
                      }`}>
                        {p.is_closed ? '마감' : '모집중'}
                      </span>
                    </td>
                    <td className="px-4 py-3 whitespace-nowrap" onClick={e => e.stopPropagation()}>
                      <div className="flex gap-1.5">
                        <button
                          onClick={() => navigate(`/admin/program/${p.program_id}`)}
                          className="text-[12px] text-[#887267] hover:text-[#6b5248] border border-[#e8d8ce] rounded px-2 py-1 bg-transparent cursor-pointer transition-colors whitespace-nowrap"
                        >수정</button>
                        <button
                          onClick={() => navigate(`/admin/program/${p.program_id}/applications`)}
                          className="text-[12px] text-[#1976d2] hover:text-[#1565c0] border border-[#bbdefb] rounded px-2 py-1 bg-transparent cursor-pointer transition-colors whitespace-nowrap"
                        >신청내역</button>
                        <button
                          onClick={e => handleExcelDownload(e, p.program_id)}
                          disabled={excelLoadingId === p.program_id}
                          className="text-[12px] text-[#388e3c] hover:text-[#2e7d32] border border-[#c8e6c9] rounded px-2 py-1 bg-transparent cursor-pointer transition-colors disabled:opacity-50 whitespace-nowrap"
                        >엑셀</button>
                        <button
                          onClick={e => handleDelete(e, p.program_id, p.program_name)}
                          disabled={deletingId === p.program_id}
                          className="text-[12px] text-red-400 hover:text-red-600 border border-red-100 rounded px-2 py-1 bg-transparent cursor-pointer transition-colors disabled:opacity-50 whitespace-nowrap"
                        >삭제</button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>

        {/* Pagination */}
        {totalPages > 1 && (
          <div className="flex justify-center items-center gap-1.5 mt-5">
            <button
              onClick={() => setSkip((currentPage - 2) * LIMIT)}
              disabled={currentPage === 1}
              className="w-8 h-8 rounded-lg text-[12px] border border-[#e8e0dc] bg-white text-[#887267] disabled:text-gray-300 disabled:cursor-not-allowed hover:bg-[#f5f2f0] cursor-pointer"
            >‹</button>
            {Array.from({ length: totalPages }, (_, i) => i + 1)
              .filter(p => p === 1 || p === totalPages || Math.abs(p - currentPage) <= 2)
              .reduce<(number | '...')[]>((acc, p, idx, arr) => {
                if (idx > 0 && typeof arr[idx - 1] === 'number' && (p as number) - (arr[idx - 1] as number) > 1)
                  acc.push('...')
                acc.push(p)
                return acc
              }, [])
              .map((p, idx) =>
                p === '...'
                  ? <span key={`e-${idx}`} className="w-8 h-8 flex items-center justify-center text-[12px] text-gray-300">…</span>
                  : <button
                      key={p}
                      onClick={() => setSkip(((p as number) - 1) * LIMIT)}
                      className={`w-8 h-8 rounded-lg text-[12px] font-medium border transition-colors cursor-pointer ${
                        currentPage === p
                          ? 'bg-[#887267] text-white border-[#887267]'
                          : 'bg-white text-[#5a3d32] border-[#e8e0dc] hover:bg-[#f5f2f0]'
                      }`}
                    >{p}</button>
              )}
            <button
              onClick={() => setSkip(currentPage * LIMIT)}
              disabled={currentPage === totalPages}
              className="w-8 h-8 rounded-lg text-[12px] border border-[#e8e0dc] bg-white text-[#887267] disabled:text-gray-300 disabled:cursor-not-allowed hover:bg-[#f5f2f0] cursor-pointer"
            >›</button>
          </div>
        )}

      </div>
    </div>
  )
}